// Design tokens del rediseño (handoff Claude Design).
// Valores que no encajan en el palette estándar de MUI (texto accesible de chips,
// fondos sutiles, sombras, radios, fuente mono). Consumir SIEMPRE desde aquí o desde
// el theme; no hardcodear colores sueltos en las vistas.

export const designTokens = {
  color: {
    primary: { main: "#0085db", light: "#e5f3fb", hover: "#0073c0" },
    text: {
      primary: "#111c2d",
      secondary: "#5A6A85",
      muted: "#7C8FAC",
      faint: "#9aa7bd",
    },
    border: {
      divider: "#e5eaef",
      light: "#eef2f6",
      lighter: "#f2f5f9",
      input: "#dfe5ef",
      outlinedBtn: "#cfe6f7",
    },
    bg: {
      default: "#F0F5F9",
      paper: "#ffffff",
      subtle: "#F7FAFD",
      subtle2: "#F2F6FA",
      zebra: "#fafcfe",
      hover: "#f6f9fc",
    },
    // Chips de estado: fondo = light (pastel), texto = oscuro accesible (NO el main).
    status: {
      success: { bg: "#dffff3", text: "#1f9d68", main: "#4bd08b" },
      info: { bg: "#e1f5fa", text: "#2ba6c4", main: "#46caeb" },
      warning: { bg: "#fff6ea", text: "#b9791f", main: "#f8c076" },
      error: { bg: "#ffede9", text: "#c7563b", main: "#fb977d", solid: "#e07a5f" },
      neutral: { bg: "#eef1f5", text: "#8a93a3", main: "#b3bccb" },
      // Azul del tema (BL-17: clientes Logosoft).
      primary: { bg: "#e5f3fb", text: "#0085db", main: "#0085db" },
    },
  },
  radius: {
    card: 18,
    panel: 16,
    table: 14,
    input: 11,
    button: 25,
    chip: 20,
    shell: 14,
  },
  shadow: {
    card: "0 7px 30px rgba(17,28,45,0.05)",
    shell: "0 9px 28px rgba(17,28,45,0.06)",
    primaryBtn: "0 6px 16px rgba(0,133,219,0.28)",
    dropdown: "0 14px 38px rgba(17,28,45,0.14)",
    modal: "0 30px 70px rgba(17,28,45,0.28)",
  },
  font: {
    mono: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  // Medidas del shell responsivo. El contenido usa todo el ancho disponible y solo
  // se topa en pantallas muy grandes (>=1536px) para no estirar las líneas de más.
  layout: {
    contentMaxWidth: 1920,
    // Alto reservado por el shell (padding de página + Header + margen del contenido).
    // Se usa como piso vía calc(100vh - x) en el área de contenido.
    chromeHeight: 170,
    // Piso de la grilla. En pantallas de 1080p o más nunca se aplica (sobra alto).
    // En laptops de 768px con barra de filtros la ventana solo deja ~340px, que son
    // 5 filas: con este piso muestra 7 y la página scrollea un poco. Es el canje
    // deliberado entre "entra todo sin scroll" y "se ven suficientes filas".
    tableMinHeight: 480,
    // Piso de los estados de carga / vacío: solo tienen que alojar un ícono y un
    // texto centrados, así que no arrastran el piso de la grilla.
    stateMinHeight: 240,
  },
} as const;

export type StatusTone = keyof typeof designTokens.color.status;

// Paleta rotativa para avatares con iniciales (color por índice/hash).
export const avatarPalette = [
  { bg: "#e5f3fb", text: "#0085db" },
  { bg: "#dffff3", text: "#1f9d68" },
  { bg: "#fff6ea", text: "#b9791f" },
  { bg: "#ffede9", text: "#c7563b" },
  { bg: "#e1f5fa", text: "#2ba6c4" },
  { bg: "#e7ecf0", text: "#5A6A85" },
] as const;

export const avatarColorFor = (seed: string | number) => {
  const s = String(seed);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};

export const initialsOf = (name: string): string =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "?";
